import { ArrowLeftOutlined, ScanOutlined } from '@ant-design/icons';
import type { ScanResolveResult, StocktakeRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatQty } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Card, Divider, Empty, Input, InputNumber, List, Select, Space, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';

const QUICK_LINKS = [
  { title: '待签收配送', path: '/delivery', desc: '整单/分批签收' },
  { title: '待出库确认', path: '/inventory/outbound', desc: '拣货复核后出库' },
  { title: '待入库确认', path: '/inventory/inbound', desc: '到货验收上架' },
  { title: '库存查询', path: '/stock', desc: '批次 / 效期 / 可用量' },
  { title: '仓间调拨', path: '/transfer', desc: '出仓确认' },
  { title: '退回处理', path: '/return', desc: '退换货取件' }
];

/**
 * PDA 精简视图（docs/05 §4.1）：无侧栏、大按钮，供库管在手持浏览器作业。
 * 只做「扫码 + 盘点采集 + 快捷入口」，写操作复用与 PC 端相同的接口。
 */
export default function PdaPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [code, setCode] = useState('');
  const [scanResult, setScanResult] = useState<ScanResolveResult | null>(null);
  const [stocktakeId, setStocktakeId] = useState<string>('');

  const scan = useMutation({
    mutationFn: (value: string) => hq.scan.resolve({ code: value, terminal: 'PDA' }),
    onSuccess: (result) => {
      setScanResult(result);
      if (!result.matched) message.warning(result.message ?? '未识别的码值');
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '扫码解析失败')
  });

  const stocktakes = useQuery({
    queryKey: ['pda', 'stocktakes'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.stocktake.list({ size: 20 })
  });

  const options = useMemo(
    () =>
      (stocktakes.data?.items ?? []).map((row: StocktakeRow) => ({
        value: row.id,
        label: `${row.bizNo}（${row.takeStatus ?? '-'}）`
      })),
    [stocktakes.data]
  );

  const detail = useQuery({
    queryKey: ['pda', 'stocktake', stocktakeId],
    enabled: Boolean(stocktakeId),
    placeholderData: keepPreviousData,
    queryFn: () => hq.stocktake.detail(stocktakeId)
  });

  const count = useMutation({
    mutationFn: (payload: { itemId: string; countQty: number }) =>
      hq.stocktake.count(stocktakeId, payload.itemId, { countQty: payload.countQty, handleType: 'ADJUST' }),
    onSuccess: async (result) => {
      message.success(`已录入：账面 ${formatQty(result.bookQty)} / 实盘 ${formatQty(result.countQty)}`);
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '录入失败')
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6f8', padding: 16 }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          PDA 作业台
        </Typography.Title>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/workbench')}>
          返回 PC
        </Button>
      </Space>

      <Card title="扫码" style={{ marginBottom: 16 }}>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            size="large"
            allowClear
            prefix={<ScanOutlined />}
            placeholder="扫描/输入单号或物资码，回车解析"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            onPressEnter={() => code.trim() && scan.mutate(code.trim())}
          />
          <Button size="large" type="primary" loading={scan.isPending} onClick={() => code.trim() && scan.mutate(code.trim())}>
            解析
          </Button>
        </Space.Compact>

        {scanResult ? (
          <Alert
            style={{ marginTop: 12 }}
            type={scanResult.matched ? 'success' : 'warning'}
            showIcon
            message={`${scanResult.scanType} ${scanResult.bizNo ?? ''} ${scanResult.status ?? ''}`}
            description={
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <span>{scanResult.summary ?? scanResult.message ?? '-'}</span>
                <Space wrap>
                  {scanResult.nextActions.map((action) => (
                    <Button
                      key={`${action.action}-${action.route}`}
                      size="small"
                      disabled={!action.route}
                      onClick={() => action.route && navigate(action.route)}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Space>
              </Space>
            }
          />
        ) : null}
      </Card>

      <Card title="快捷作业" style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
          {QUICK_LINKS.map((item) => (
            <Button key={item.path} size="large" style={{ height: 88 }} onClick={() => navigate(item.path)}>
              <Space direction="vertical" size={2}>
                <span style={{ fontSize: 16, fontWeight: 600 }}>{item.title}</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {item.desc}
                </Typography.Text>
              </Space>
            </Button>
          ))}
        </div>
      </Card>

      <Card title="盘点采集">
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            style={{ minWidth: 260 }}
            size="large"
            placeholder="选择盘点单"
            loading={stocktakes.isPending}
            options={options}
            value={stocktakeId || undefined}
            onChange={(value) => setStocktakeId(value)}
          />
          <Button size="large" onClick={() => stocktakes.refetch()} loading={stocktakes.isFetching}>
            刷新
          </Button>
          {detail.data ? <Tag>盘点单 {detail.data.stocktake.bizNo}</Tag> : null}
        </Space>

        {detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="盘点单加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : null}

        {stocktakeId && detail.data ? (
          <List
            dataSource={detail.data.items}
            locale={{ emptyText: <Empty description="该盘点单无明细" /> }}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <CountEditor
                    key="editor"
                    bookQty={item.bookQty}
                    countQty={item.countQty}
                    loading={count.isPending}
                    onSubmit={(qty) => count.mutate({ itemId: item.id, countQty: qty })}
                  />
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space wrap>
                      <span>{item.materialName ?? item.materialCode ?? item.materialId}</span>
                      {item.batchNo ? <Tag>批次 {item.batchNo}</Tag> : null}
                    </Space>
                  }
                  description={
                    <Space split={<Divider type="vertical" />} wrap>
                      <span>编码 {item.materialCode ?? '-'}</span>
                      <span>账面 {formatQty(item.bookQty)}</span>
                      <span>已录 {formatQty(item.countQty)}</span>
                      <span>差异 {formatQty(item.diffQty)}</span>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Typography.Text type="secondary">选择盘点单后逐行录入实盘数，确认盘点在 PC 端完成。</Typography.Text>
        )}
      </Card>
    </div>
  );
}

function CountEditor({
  bookQty,
  countQty,
  loading,
  onSubmit
}: {
  bookQty: number;
  countQty: number;
  loading: boolean;
  onSubmit: (qty: number) => void;
}) {
  const [value, setValue] = useState<number>(countQty || bookQty);
  return (
    <Space>
      <InputNumber
        size="large"
        min={0}
        precision={3}
        value={value}
        onChange={(next) => setValue(typeof next === 'number' ? next : 0)}
        style={{ width: 120 }}
        aria-label="实盘数量"
      />
      <Button size="large" type="primary" loading={loading} onClick={() => onSubmit(value)}>
        录入
      </Button>
    </Space>
  );
}
